<template>
  <div class="iframes open-iframe">
    <div class="iframes__details">
      <div v-if="!isLoading" class="toggle-icons">
        <div class="iframes__icon-wrapper">
          <IFrameIcon
            :fill="hasWatched ? '#00DEA0' : '#808080'"
            class="iframes__icon"
          />
        </div>
        <div class="toggle-icons__check-wrapper">
          <CheckIcon
            :fill="hasWatched ? '#00DEA0' : '#727276'"
            class="toggle-icons__check"
          />
          <button
            class="toggle-icons__mark-as-finish"
            @click="toggleIFrameStatus()"
          >
            <span v-if="!hasWatched">Mark as finished</span>
            <span v-else>Mark as unfinished</span>
          </button>
        </div>
      </div>
      <div v-else class="iframes__loading-wrapper">
        <ActionLoader
          loaderClass="iframes__loading-spinner"
          name="ball-clip-rotate"
        />
      </div>
      <div
        class="iframes__length-wrapper"
        v-if="content.metadata && content.metadata.slides_count"
      >
        <p class="iframes__length">
          {{
            content.metadata && content.metadata.slides_count
              ? content.metadata.slides_count + ' slides'
              : null
          }}
        </p>
      </div>
      <div class="iframes__report-issue" @click.stop="startIssue">
        <IssueIcon class="iframes__report-issue__icon" />
        <span class="iframes__report-issue__text">Report an issue</span>
      </div>
      <div
        class="fullscreen pointer"
        v-if="supportFullScreen"
        @click="toggleFullscreen()"
      >
        <FullscreenIcon class="fulscreen__icon" />
        <span class="fullscreen__text">Fullscreen</span>
      </div>
    </div>
    <div class="iframes__player-wrapper">
      <iframe
        v-if="iframeURL"
        ref="iframe"
        class="iframes__window"
        :src="iframeURL"
        frameborder="0"
        marginheight="0"
        marginwidth="0"
      ></iframe>
      <Files
        class="iframe-files"
        :content="content"
        :canOpen="false"
        :transparent="true"
      />
    </div>
  </div>
</template>

<script>
import ActionLoader from '@/components/shared/ActionLoader.vue'
import IFrameIcon from '@/assets/icons/course/iframe/iframe-icon.svg'
import FullscreenIcon from '@/assets/icons/course/iframe/fullscreen-icon.svg'
import Files from '@/components/modals/exercise-modal/Files.vue'
import CheckIcon from '@/assets/icons/course/exercise/check-circle.svg'
import IssueIcon from '@/assets/icons/course/bug.svg'
import CONSTANTS from '@/constants'

export default {
  name: 'IFrame',
  components: {
    IFrameIcon: { ...IFrameIcon, name: 'IFrameIcon' },
    FullscreenIcon: { ...FullscreenIcon, name: 'FullscreenIcon' },
    ActionLoader,
    Files,
    CheckIcon: { ...CheckIcon, name: 'CheckIcon' },
    IssueIcon: { ...IssueIcon, name: 'IssueIcon' },
  },
  data() {
    return {
      isLoading: false,
      iframeURL: '',
    }
  },
  props: {
    content: {
      type: Object,
      required: true,
    },
    userCompletionStatus: {
      type: String,
      required: true,
    },
    parentType: {
      type: String,
      required: false,
      default: () => 'isolated',
    },
    parent: {
      type: Object,
      required: false,
    },
  },
  computed: {
    hasWatched() {
      return (
        this.userCompletionStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
      )
    },
  },
  methods: {
    toggleFullscreen() {
      if (!this.fullScreenStatus()) {
        this.launchIntoFullscreen(this.$refs.iframe)
      } else {
        this.exitFullscreen()
      }
    },
    launchIntoFullscreen(element) {
      if (element.requestFullscreen) {
        element.requestFullscreen()
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen()
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen()
      }
    },
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    },
    fullScreenStatus() {
      if (
        document.mozFullScreen ||
        document.fullscreenElement ||
        document.msFullscreenElement ||
        document.webkitIsFullScreen
      ) {
        return true
      }
      return false
    },
    supportFullScreen() {
      const doc = document.documentElement

      return (
        'requestFullscreen' in doc ||
        ('mozRequestFullScreen' in doc && document.mozFullScreenEnabled) ||
        ('msRequestFullscreen' in doc && document.msFullscreenEnabled) ||
        'webkitRequestFullScreen' in doc
      )
    },
    scrollToOpenIframe() {
      if (!this.fullScreenStatus()) {
        setTimeout(() => {
          this.$scrollTo('.open-iframe', 10, {
            container: 'body',
            offset: -220,
          })
        }, 10)
      }
    },
    async toggleIFrameStatus() {
      this.isLoading = true
      const updatedStatus =
        this.userCompletionStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
          ? CONSTANTS.CONTENT_USER_STATUS.UNSTARTED
          : CONSTANTS.CONTENT_USER_STATUS.FINISHED
      await this.$store.dispatch('content/updateIFrameStatus', {
        iframeId: this.content.id,
        status: updatedStatus,
      })
      if (
        this.parentType === 'course' &&
        updatedStatus === CONSTANTS.CONTENT_USER_STATUS.FINISHED
      ) {
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_IFRAME_FINISHED, {
          course_id: this.parent.id,
          course_name: this.parent.name,
          iframe_id: this.content.id,
          iframe_name: this.content.name,
          ...this.content,
        })
        setTimeout(async () => {
          await this.$store.dispatch('courses/getCourseById', this.parent.id)
        }, 500)
      }
      this.isLoading = false
    },
    startIssue() {
      this.$store.dispatch('courses/saveContentIssue', {
        content_type: 'iframe',
        content_subtype: this.content.type,
        content_name: this.content.name,
        content_id: this.content.id,
        extra_args: {
          iframe_cdn_url: this.iframeURL,
        },
      })
    },
  },
  async created() {
    this.$emit('is-loading', true)
    const response = await this.$store.dispatch(
      'iframes/getSignedIFrameURL',
      this.content.id
    )
    if (!this.hasWatched) {
      await this.$store.dispatch('content/updateIFrameStatus', {
        iframeId: this.content.id,
        status: CONSTANTS.CONTENT_USER_STATUS.STARTED,
      })
      if (this.parentType === 'course') {
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_IFRAME_STARTED, {
          course_id: this.parent.id,
          course_name: this.parent.name,
          iframe_id: this.content.id,
          iframe_name: this.content.name,
          ...this.content,
        })
      }
    }
    this.iframeURL = response.url
    this.$emit('is-loading', false)
    if (this.parentType === 'course') {
      this.sendMixpanelEvent(
        this.eventsConstants.CONTENT_IFRAME_MEDIA_REQUESTED,
        {
          course_id: this.parent.id,
          course_name: this.parent.name,
          iframe_id: this.content.id,
          iframe_name: this.content.name,
          ...this.content,
        }
      )
    }
    document.addEventListener('fullscreenchange', this.scrollToOpenIframe)
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.scrollToOpenIframe)
  },
  mounted() {
    this.$scrollTo('.open-iframe', 1000, {
      container: 'body',
      offset: -220,
    })
    setTimeout(() => {
      this.$emit('is-course-active')
    }, 15000)
  },
}
</script>

<style lang="scss">
.iframes {
  $this: &;
  margin: 0 auto 50px 0;
  transition: 1s all ease;
  position: relative;
  &__details {
    display: flex;
    align-items: center;
    margin: 20px 0 20px 0;
    flex-wrap: wrap;
    @include mobile {
      flex-wrap: nowrap;
    }
  }
  &__icon-wrapper {
    background: var(--light-gray);
    height: 62px;
    width: 62px;
    margin: 0 5px 0 0;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__loading-wrapper {
    width: 62px;
    height: 62px;
    text-align: center;
  }
  &__loading-spinner {
    margin: 5px 0 0 0;
  }
  &__icon {
    vertical-align: middle;
  }
  &__length-wrapper {
    display: flex;
    align-items: center;
    margin: 5px 0 0 50px;
  }
  &__length {
    color: var(--white);
  }
  &__player-wrapper {
    @include desktop {
      padding: 0 50px;
    }
    width: 100%;
  }
  &__window {
    width: 100%;
    min-height: 80vh;
    background: rgb(214, 214, 214);
    @include lg-desktop {
      min-height: 700px;
    }
  }
  .pointer {
    cursor: pointer;
  }
  .fullscreen {
    @include mobile {
      padding-right: 30px;
    }
    color: white;
    display: flex;
    align-content: center;
    &__text {
      padding-left: 10px;
    }
  }
  .toggle-icons {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
    @include mobile {
      width: 250px;
      justify-content: initial;
    }
    &__check-wrapper {
      display: flex;
    }
    &__check {
      margin-left: 20px;
    }
    &__mark-as-finish {
      background: transparent;
      color: var(--white);
      font-weight: 700;
      border: none;
      padding: 0px;
      margin: 0 0 0 10px;
      cursor: pointer;
    }
  }
  &__report-issue {
    color: white;
    margin-left: auto;
    margin-right: 15px;
    display: flex;
    align-items: center;
    cursor: pointer;
    &__icon {
      fill: #717171;
      margin-right: 5px;
    }
  }
}
</style>
